/**
 * EditCustomerPanel (EDIT-SUITE-01, Seth 2026-10-22: "when I click edit a
 * customer I expect a full customer edit suite — everything the 360 shows,
 * findable under edit, all connected, uniform") — ONE suite, nine sections in
 * the 360's order, opened from every pencil on the page at that section.
 *
 *   Name & Account · People & Lines · Reach · Properties · Watch For ·
 *   Equipment · Plan · Notes & Snapshot · Links & History
 *
 * Saves as you go (DEC-27): a field writes when you leave it, a chip when you
 * tap it; the header reads "All changes saved ✓". No Save button anywhere.
 * The editors the office already knows (Watch For, the unit editor) live here
 * unchanged — one editor per fact, reached from wherever you are.
 *
 * ESC is never written (DEC-22). An ESC fact is corrected by a Pegasus row that
 * every reader prefers: the shown name (pegasus_customer_override.display_name,
 * merged into the record by useCustomer), a phone or email (a `contact` line
 * that REPLACES the ESC line — CONTACT-ONE-TABLE), a status, a type.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import {
  Check, ChevronDown, ChevronUp, Mail, PenLine, Phone, Plus, RotateCcw, Star, UserRound, X, Home, Receipt, ArrowRight, Trash2,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { displayCustomerName } from "@/lib/format";
import { toastError } from "@/components/ui/mutationToast";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { useActiveTechs } from "@/features/board/api";
import { useLocationOwnership } from "@/features/jobs/properties";
import { CommsPrefSwitches } from "@/features/comms/CommsPrefButton";
import { useCommsPref, useSaveCommsPref } from "@/features/comms/comms";
import { useCustomerConsent } from "@/features/comms/consent";
import { useMembershipStatus } from "@/features/agreements/api";
import {
  useCustomerSignals, useAddSignal, useUpdateSignalValue, useResolveSignal,
  useCustomerStatusFlag, useSnapshotNote, useSaveSnapshotNote,
  type CustomerWithLocations, type LocationRow,
} from "./api";
import { useContacts, type ContactRow } from "./CustomerContacts";
import { buildLinesDetailed, splitLinesByEra, contactEraOf, lineKeys, type Line } from "./contactLines";
import { activeTechRequests, currentTechRequest, rosterTechsFor, setTechRequest, techFirst, techLabel } from "./techRequest";
import { useLeadSourceOptions, useCustomerLeadSource, useSetCustomerLeadOverride } from "./leadSource";
import { WatchOutsEditorBody } from "./WatchOutsEditor";
import { CustomerEquipment } from "./CustomerEquipment";
import { usePegasusNotes, useAddPegasusNote, useDeletePegasusNote } from "./pegasusNotes";
import { isDoNotService } from "./api";
import { EDIT_SECTIONS, type EditSection } from "./editCustomerContext";

// ---------------------------------------------------------------------------
// Save state — "All changes saved ✓" is a fact, not a decoration.
// ---------------------------------------------------------------------------
interface SaveCtx { track: <T>(p: Promise<T>) => Promise<T | undefined> }
const SaveContext = createContext<SaveCtx>({ track: async (p) => p });
const useTrack = () => useContext(SaveContext).track;

function fmtTime(d: Date) {
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }).toLowerCase();
}

// ---------------------------------------------------------------------------
// Field chrome — one look for every section (the modal's label + input, kept).
// ---------------------------------------------------------------------------
const LABEL = "text-[10.5px] font-extrabold uppercase tracking-[0.1em] text-slate-500 flex items-center gap-2";
const INPUT = "mt-1.5 w-full rounded-[10px] border border-hairline bg-white px-3 py-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/25 disabled:bg-slate-50 disabled:text-slate-500";
const SUB = "mt-1.5 text-[11.5px] text-slate-400";

function Field({ label, hint, children, className }: { label: ReactNode; hint?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={className}>
      <div className={LABEL}>{label}</div>
      {children}
      {hint && <div className={SUB}>{hint}</div>}
    </div>
  );
}

/** A text field that writes when you leave it (or press Enter) — only when the value changed. */
function TextField({ value, onCommit, placeholder, disabled, mono, autoFocus, type = "text" }: {
  value: string; onCommit: (v: string) => void | Promise<void>; placeholder?: string; disabled?: boolean; mono?: boolean; autoFocus?: boolean; type?: string;
}) {
  const [v, setV] = useState(value);
  const last = useRef(value);
  useEffect(() => { if (value !== last.current) { last.current = value; setV(value); } }, [value]);
  const commit = () => { if (v.trim() !== last.current.trim()) { last.current = v; void onCommit(v.trim()); } };
  return (
    <input
      type={type} value={v} disabled={disabled} placeholder={placeholder} autoFocus={autoFocus}
      onChange={(e) => setV(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); (e.target as HTMLInputElement).blur(); } }}
      className={cn(INPUT, mono && "font-mono")}
    />
  );
}

function Segment<T extends string>({ value, options, onChange, disabled }: { value: T; options: { key: T; label: string; tone?: "bad" }[]; onChange: (k: T) => void; disabled?: boolean }) {
  return (
    <div className="mt-1.5 inline-flex gap-0.5 rounded-[11px] bg-slate-100 p-[3px]">
      {options.map((o) => {
        const on = o.key === value;
        return (
          <button key={o.key} type="button" disabled={disabled} onClick={() => !on && onChange(o.key)}
            className={cn("rounded-[9px] px-3.5 py-1.5 text-[12.5px] font-bold transition-colors disabled:opacity-60",
              on ? (o.tone === "bad" ? "bg-white text-rose-700 shadow-sm" : "bg-white text-brand-700 shadow-sm") : "text-slate-500 hover:text-slate-800")}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Tag({ kind }: { kind: "esc" | "pegasus" | "owner" }) {
  return (
    <span className={cn("inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-extrabold uppercase tracking-[0.06em]",
      kind === "esc" ? "bg-slate-100 text-slate-500" : kind === "owner" ? "bg-amber-50 text-amber-800" : "bg-brand-50 text-brand-700")}>
      {kind === "esc" ? "ESC" : kind === "owner" ? "Owner" : "Pegasus"}
    </span>
  );
}

function SectionHead({ title, lead, right }: { title: string; lead?: string; right?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-3">
      <h3 className="font-display text-[17px] font-extrabold text-slate-900">{title}</h3>
      {lead && <span className="text-[12.5px] text-slate-500">{lead}</span>}
      {right && <span className="ml-auto">{right}</span>}
    </div>
  );
}

const prettyPhone = (s: string) => { const d = s.replace(/\D/g, ""); return d.length === 10 ? `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}` : d.length === 11 && d[0] === "1" ? `${d.slice(1, 4)}-${d.slice(4, 7)}-${d.slice(7)}` : s; };
const initialsOf = (name: string | null) => (name ?? "").trim().split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? "").join("") || "—";

// ---------------------------------------------------------------------------
// The panel
// ---------------------------------------------------------------------------
export interface EditCustomerPanelProps {
  customer: CustomerWithLocations;
  selectedLocation: LocationRow | null;
  open: boolean;
  section: EditSection | null;
  onClose: () => void;
  /** Jump to a 360 tab (the Plan section's door to Manage Plan; the Comms tab for the portal). */
  onGoTab?: (tab: string) => void;
  /** Links & History: the header owns these modals — open them after the panel closes. */
  onLinkAction?: (kind: "transfer" | "link" | "merge" | "previous" | "addProperty" | "moveProperty", loc?: LocationRow) => void;
}

export function EditCustomerPanel({ customer, selectedLocation, open, section, onClose, onGoTab, onLinkAction }: EditCustomerPanelProps) {
  const [saving, setSaving] = useState(0);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [failed, setFailed] = useState(false);
  const track = useCallback(async <T,>(p: Promise<T>): Promise<T | undefined> => {
    setSaving((n) => n + 1);
    try { const r = await p; setLastSaved(new Date()); setFailed(false); return r; }
    catch (e) { setFailed(true); toastError(`Couldn't save — ${(e as Error)?.message ?? "try again"}`); return undefined; }
    finally { setSaving((n) => n - 1); }
  }, []);
  const ctx = useMemo<SaveCtx>(() => ({ track }), [track]);

  const bodyRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<EditSection>(section ?? "account");
  const jumping = useRef(false);
  const jump = useCallback((s: EditSection, smooth = true) => {
    const el = bodyRef.current?.querySelector<HTMLElement>(`[data-section="${s}"]`);
    if (!el || !bodyRef.current) return;
    jumping.current = true;
    setActive(s);
    bodyRef.current.scrollTo({ top: el.offsetTop - 12, behavior: smooth ? "smooth" : "auto" });
    window.setTimeout(() => { jumping.current = false; }, smooth ? 500 : 50);
  }, []);
  // Open at the section the door asked for — after the slide-in has laid out.
  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => jump(section ?? "account", false), 280);
    return () => window.clearTimeout(t);
  }, [open, section, jump]);
  const onScroll = () => {
    if (jumping.current || !bodyRef.current) return;
    const top = bodyRef.current.scrollTop + 80;
    let cur: EditSection = "account";
    for (const s of EDIT_SECTIONS) {
      const el = bodyRef.current.querySelector<HTMLElement>(`[data-section="${s.key}"]`);
      if (el && el.offsetTop <= top) cur = s.key;
    }
    setActive(cur);
  };
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if ((document.activeElement as HTMLElement | null)?.getAttribute("aria-expanded") === "true") return;
      onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);

  const locno = selectedLocation?.locno ?? null;
  const { data: owned } = useLocationOwnership(customer.custno, locno);
  const shownName = owned?.currentOwner?.name ?? displayCustomerName(null, customer.fullname);
  const { data: contacts } = useContacts(customer.custno, locno);
  const peopleCount = useMemo(() => buildLinesDetailed(customer, selectedLocation, contacts ?? []).lines.length, [customer, selectedLocation, contacts]);
  const { data: signals } = useCustomerSignals(customer.custno);
  const watchCount = (signals ?? []).filter((s) => s.status === "confirmed" && !s.hidden_at && !s.archived_at && s.signal_type !== "nickname" && s.signal_type !== "prior_owner" && (!s.locno || !locno || s.locno === locno)).length;
  const propCount = (customer.locations ?? []).filter((l) => !l.location_inactive).length;
  const counts: Partial<Record<EditSection, number>> = { people: peopleCount, watch: watchCount, properties: propCount };

  // Rendered on <body>: the header band that opens the panel is a sticky z-30 stacking
  // context, and the app's top bar (z-40) would paint over the panel's header otherwise.
  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[70]">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}
            className="absolute inset-0 bg-slate-900/30 backdrop-blur-[1px]" onClick={onClose} />
          <motion.div
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
            className="absolute right-0 top-0 flex h-full w-full max-w-[960px] flex-col bg-white shadow-2xl"
            role="dialog" aria-modal="true" aria-label="Edit Customer"
          >
            <header className="flex items-center gap-3.5 border-b border-hairline px-6 py-4">
              <span className="grid h-9 w-9 place-items-center rounded-[10px] bg-brand-50 text-brand-600"><PenLine className="h-[18px] w-[18px]" /></span>
              <div className="min-w-0">
                <div className="font-display text-[18px] font-extrabold leading-tight text-slate-900">Edit Customer</div>
                <div className="truncate text-xs text-slate-500">{shownName} · #{customer.custno} · {customer.native ? "Pegasus record" : "ESC record + Pegasus corrections"}</div>
              </div>
              <span className={cn("ml-auto inline-flex items-center gap-1.5 text-xs font-bold", failed ? "text-rose-700" : saving > 0 ? "text-slate-400" : "text-emerald-700")}>
                {saving > 0 ? "Saving…" : failed ? "Couldn't save — see the message" : <><Check className="h-3.5 w-3.5" strokeWidth={2.6} /> All changes saved{lastSaved ? ` · ${fmtTime(lastSaved)}` : ""}</>}
              </span>
              <button type="button" onClick={onClose} className="rounded-[10px] border border-hairline bg-white px-3 py-1.5 text-[12.5px] font-bold text-slate-700 shadow-e1 hover:bg-slate-50">Done</button>
            </header>

            <div className="grid min-h-0 flex-1 grid-cols-[208px_minmax(0,1fr)]">
              <nav className="border-r border-hairline bg-[#fafbfc] px-2.5 py-3.5">
                {EDIT_SECTIONS.map((s) => {
                  const on = active === s.key;
                  const n = counts[s.key];
                  return (
                    <button key={s.key} type="button" onClick={() => jump(s.key)}
                      className={cn("flex w-full items-center gap-2.5 rounded-[10px] px-3 py-2 text-left text-[13px] font-semibold",
                        on ? "border border-hairline bg-white text-brand-700 shadow-sm" : "border border-transparent text-slate-600 hover:bg-slate-100")}>
                      <span className={cn("h-2 w-2 rounded-full", on ? "bg-brand-600" : "bg-slate-300")} />
                      {s.label}
                      {n !== undefined && n > 0 && <span className={cn("ml-auto rounded-full px-1.5 text-[10.5px] font-extrabold", on ? "bg-brand-50 text-brand-700" : "bg-slate-100 text-slate-400")}>{n}</span>}
                    </button>
                  );
                })}
                <div className="mx-3 mt-4 text-[11px] leading-relaxed text-slate-400">The same order as the 360 · every pencil on the page opens here</div>
              </nav>

              <SaveContext.Provider value={ctx}>
                <div ref={bodyRef} onScroll={onScroll} className="relative min-h-0 overflow-y-auto px-7 pb-10 pt-5">
                  <div className="flex flex-col gap-9">
                    <section data-section="account"><AccountSection customer={customer} selectedLocation={selectedLocation} /></section>
                    <section data-section="people"><PeopleSection customer={customer} selectedLocation={selectedLocation} /></section>
                    <section data-section="reach"><ReachSection custno={customer.custno} onGoTab={onGoTab} onClose={onClose} /></section>
                    <section data-section="properties"><PropertiesSection customer={customer} selectedLocation={selectedLocation} onLinkAction={onLinkAction} onClose={onClose} onJumpWatch={() => jump("watch")} /></section>
                    <section data-section="watch"><WatchSection customer={customer} locno={locno} /></section>
                    <section data-section="equipment">
                      <SectionHead title="Equipment" lead="the unit editor from the Equipment tab — the same one" />
                      <div className="mt-3"><CustomerEquipment custno={customer.custno} locno={locno} onOpenAgreement={() => { onClose(); onGoTab?.("membership"); }} /></div>
                    </section>
                    <section data-section="plan"><PlanSection custno={customer.custno} onGoTab={onGoTab} onClose={onClose} /></section>
                    <section data-section="notes"><NotesSection customer={customer} locno={locno} /></section>
                    <section data-section="links"><LinksSection onLinkAction={onLinkAction} onClose={onClose} hasPrevious={(owned?.previousOwners?.length ?? 0) > 0} /></section>
                  </div>
                </div>
              </SaveContext.Provider>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// 1 · Name & Account
// ---------------------------------------------------------------------------
function AccountSection({ customer, selectedLocation }: { customer: CustomerWithLocations; selectedLocation: LocationRow | null }) {
  const track = useTrack();
  const qc = useQueryClient();
  const custno = customer.custno;
  const locno = selectedLocation?.locno ?? null;
  const canStatus = useCan("customers.mark_inactive");
  const { data: owned } = useLocationOwnership(custno, locno);
  const owner = owned?.currentOwner?.source === "pegasus" ? owned.currentOwner : null;
  const { data: statusFlag } = useCustomerStatusFlag(custno);
  const { data: signals } = useCustomerSignals(custno);
  const { data: techs } = useActiveTechs("03");
  const roster = useMemo(() => (techs ?? []).slice().sort((a, b) => techFirst(a).localeCompare(techFirst(b))), [techs]);
  const addSignal = useAddSignal();
  const updateSignal = useUpdateSignalValue();
  const resolveSignal = useResolveSignal();
  const setLead = useSetCustomerLeadOverride();
  const { data: leadOptions } = useLeadSourceOptions();
  const { data: lead } = useCustomerLeadSource(custno);
  const who = async () => (await supabase.auth.getUser()).data.user?.email ?? null;
  const invalidateRecord = () => {
    qc.invalidateQueries({ queryKey: ["customer", custno] });
    qc.invalidateQueries({ queryKey: ["customer_override", custno] });
    qc.invalidateQueries({ queryKey: ["pegasus_customer_row", custno] });
    qc.invalidateQueries({ queryKey: ["customer_search"] });
    qc.invalidateQueries({ queryKey: ["location_ownership"] });
  };

  // --- Name: written to the home the shown name comes from.
  const shownName = owner?.name ?? displayCustomerName(null, customer.fullname);
  const escName = customer.esc_fullname ? displayCustomerName(null, customer.esc_fullname) : null;
  const saveName = (v: string) => track((async () => {
    const value = v.trim();
    if (owner) {
      if (!value) return;
      const { error } = await supabase.from("pegasus_owner").update({ name: value }).eq("id", owner.id);
      if (error) throw error;
    } else if (customer.native) {
      if (!value) return;
      const { error } = await supabase.from("pegasus_customer").update({ fullname: value, updated_at: new Date().toISOString() }).eq("custno", custno);
      if (error) throw error;
    } else {
      // ESC's name verbatim = no correction; a different name is the correction everyone sees.
      const display = value && value !== escName ? value : null;
      const { error } = await supabase.from("pegasus_customer_override").upsert(
        { custno, display_name: display, updated_at: new Date().toISOString(), updated_by: await who() }, { onConflict: "custno" });
      if (error) throw error;
    }
    invalidateRecord();
  })());
  const nameHint = owner
    ? <>Recorded owner of this property{escName ? <> · ESC still has <b className="font-semibold text-slate-500">{escName}</b></> : null}</>
    : customer.native ? "Pegasus record"
    : <>ESC has <b className="font-semibold text-slate-500">{escName}</b> · ESC keeps its copy until cutover</>;

  // --- Goes by: the nickname watch-out, edited in place.
  const nick = useMemo(() => {
    const rows = (signals ?? []).filter((s) => s.signal_type === "nickname" && s.status !== "dismissed" && !s.hidden_at && !s.archived_at && !!s.value);
    return rows.find((s) => s.status === "confirmed") ?? rows[0] ?? null;
  }, [signals]);
  const saveNick = (v: string) => track((async () => {
    if (nick && !v) await resolveSignal.mutateAsync({ id: nick.id, status: "dismissed" });
    else if (nick && v) await updateSignal.mutateAsync({ id: nick.id, value: v, custno });
    else if (v) await addSignal.mutateAsync({ custno, signal_type: "nickname", value: v });
  })());

  // --- Type: a Pegasus fact, seeded from ESC's class.
  const escClass = (selectedLocation?.custom_1 ?? (customer.locations ?? []).find((l) => (l.custom_1 ?? "").trim())?.custom_1 ?? "").trim().toUpperCase();
  const escType: "residential" | "commercial" = /COMM/.test(escClass) ? "commercial" : "residential";
  const type = customer.customer_type ?? escType;
  const saveType = (t: "residential" | "commercial") => track((async () => {
    if (customer.native) {
      const { error } = await supabase.from("pegasus_customer").update({ customer_type: t, updated_at: new Date().toISOString() }).eq("custno", custno);
      if (error) throw error;
    } else {
      const { error } = await supabase.from("pegasus_customer_override").upsert(
        { custno, customer_type: t, updated_at: new Date().toISOString(), updated_by: await who() }, { onConflict: "custno" });
      if (error) throw error;
    }
    invalidateRecord();
  })());

  // --- Status: Active | Inactive | Do Not Service — both flags written together.
  const escDns = isDoNotService(null, customer.credit_rating);
  const escInactive = !!customer.customerinactive && !customer.native;
  const status: "active" | "inactive" | "dns" = statusFlag?.do_not_service || escDns ? "dns" : statusFlag?.inactive || escInactive ? "inactive" : "active";
  const fromEsc = (status === "dns" && !statusFlag?.do_not_service && escDns) || (status === "inactive" && !statusFlag?.inactive && escInactive);
  const [pendingStatus, setPendingStatus] = useState<"inactive" | "dns" | null>(null);
  const [reason, setReason] = useState("");
  const writeStatus = (s: "active" | "inactive" | "dns", why: string | null) => track((async () => {
    const { error } = await supabase.from("pegasus_customer_status").upsert({
      custno,
      inactive: s === "inactive", reason: s === "inactive" ? why : null,
      do_not_service: s === "dns", dns_reason: s === "dns" ? why : null,
      updated_at: new Date().toISOString(), updated_by: await who(),
    }, { onConflict: "custno" });
    if (error) throw error;
    qc.invalidateQueries({ queryKey: ["customer_status_flag", custno] });
    qc.invalidateQueries({ queryKey: ["customer_search"] });
    setPendingStatus(null); setReason("");
  })());

  // --- Requests a Tech
  const current = useMemo(() => currentTechRequest(signals, roster), [signals, roster]);
  const proposed = useMemo(() => activeTechRequests(signals).find((s) => s.status === "proposed") ?? null, [signals]);
  const techValue = current?.techs[0]?.empno ?? "";
  const saveTech = (empno: string) => track((async () => {
    const t = roster.find((x) => x.empno === empno);
    await setTechRequest({ custno, picks: t ? [{ empno: t.empno, first: techFirst(t) }] : [], signals, source: "edit_customer" });
    qc.invalidateQueries({ queryKey: ["customer_signals", custno] });
  })());
  const techHint = current && current.techs.length > 1 ? `On file: ${current.techs.map(techFirst).join(" or ")} · picking one replaces both`
    : current && current.techs.length === 0 ? `On file as typed: “${current.row.value}” · picking a tech replaces it`
    : !current && proposed ? `Found in old notes: ${rosterTechsFor(proposed, roster).map(techFirst).join(" or ") || proposed.value} · not confirmed · pick to confirm`
    : "Badge · Book a Call · the tech app read it";

  const leadCode = lead?.overrideCode ?? "";
  const leadHint = lead?.overrideCode ? "Corrected in Pegasus" : lead?.originalCode ? `First on a dispatch or invoice: ${leadOptions?.find((o) => o.code === lead.originalCode)?.label ?? lead.originalCode}${lead.originalDate ? ` · ${lead.originalDate.slice(0, 4)}` : ""} — until you set one` : "First source on a dispatch or invoice wins until you set one";

  return (
    <div>
      <SectionHead title="Name & Account" lead="who this is, and the state of the account" />
      <div className="mt-4 grid grid-cols-2 gap-x-5 gap-y-4">
        <Field className="col-span-2" label={<>Name <span className="font-semibold normal-case tracking-normal text-slate-400">· shown everywhere in Pegasus</span></>} hint={nameHint}>
          <TextField value={shownName} onCommit={saveName} />
        </Field>
        <Field label="Goes by" hint={nick?.status === "proposed" ? "Found in your notes · saving it confirms it" : undefined}>
          <TextField value={nick?.value ?? ""} onCommit={saveNick} placeholder={shownName.split(" ")[0]} />
        </Field>
        <Field label="Type" hint={customer.customer_type ? undefined : `From ESC${escClass ? ` (${escClass})` : ""} · set it here to make it a Pegasus fact`}>
          <Segment value={type} onChange={saveType} options={[{ key: "residential", label: "Residential" }, { key: "commercial", label: "Commercial" }]} />
        </Field>
        <Field label="Status" hint={fromEsc ? "Marked in ESC — change it there, or set a Pegasus status here to take over" : !canStatus ? "Changing the status needs the customers permission" : pendingStatus ? undefined : status === "active" ? "A reason is asked for when you move it" : (status === "dns" ? statusFlag?.dns_reason : statusFlag?.reason) || "No reason recorded"}>
          <Segment<"active" | "inactive" | "dns"> value={pendingStatus ?? status} disabled={!canStatus}
            onChange={(k) => { if (k === "active") void writeStatus("active", null); else { setPendingStatus(k); setReason(""); } }}
            options={[{ key: "active", label: "Active" }, { key: "inactive", label: "Inactive" }, { key: "dns", label: "Do Not Service", tone: "bad" }]} />
          {pendingStatus && (
            <div className="mt-2 flex items-center gap-2">
              <input autoFocus value={reason} onChange={(e) => setReason(e.target.value)} placeholder={pendingStatus === "dns" ? "Why — unpaid balance since 2024, abusive to techs" : "Why — moved away, duplicate of 1007902"}
                onKeyDown={(e) => { if (e.key === "Enter") void writeStatus(pendingStatus, reason.trim() || null); if (e.key === "Escape") setPendingStatus(null); }}
                className={cn(INPUT, "mt-0 flex-1")} />
              <button type="button" onClick={() => void writeStatus(pendingStatus, reason.trim() || null)} className={cn("rounded-[10px] px-3 py-2 text-[12.5px] font-bold text-white", pendingStatus === "dns" ? "bg-rose-700 hover:bg-rose-800" : "bg-brand-700 hover:bg-brand-800")}>{pendingStatus === "dns" ? "Mark Do Not Service" : "Mark Inactive"}</button>
              <button type="button" onClick={() => setPendingStatus(null)} className="text-[12.5px] font-semibold text-slate-500 hover:text-slate-800">Cancel</button>
            </div>
          )}
        </Field>
        <Field label="Lead Source" hint={leadHint}>
          <select value={leadCode} onChange={(e) => void track(setLead.mutateAsync({ custno, code: e.target.value || null }))} className={cn(INPUT, "bg-white")}>
            <option value="">{lead?.originalCode ? "Not set — using the first recorded" : "Not recorded"}</option>
            {(leadOptions ?? []).filter((o) => o.active || o.code === leadCode).map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
          </select>
        </Field>
        <Field label="Customer #">
          <div className={cn(INPUT, "flex items-center gap-2 bg-slate-50 font-mono text-slate-600")}>{custno}<Tag kind={customer.native ? "pegasus" : "esc"} /></div>
        </Field>
        <Field className="col-span-2" label={<span className="inline-flex items-center gap-1"><UserRound className="h-3 w-3 text-amber-600" /> Requests a Tech</span>} hint={techHint}>
          <select value={techValue} onChange={(e) => void saveTech(e.target.value)} disabled={signals === undefined || techs === undefined} className={cn(INPUT, "bg-white")}>
            <option value="">No preference</option>
            {roster.map((t) => <option key={t.empno} value={t.empno}>{techLabel(t)}</option>)}
          </select>
        </Field>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 2 · People & Lines — one list; ESC lines as ESC has them, Pegasus lines editable
// ---------------------------------------------------------------------------
const LINE_LABELS = ["Cell", "Home", "Work", "Spouse", "Office", "Main", "Other"];
const ROLE_SUGGESTIONS = ["Owner", "Spouse", "Tenant", "Property manager", "Maintenance", "AP / Billing", "Nanny", "Caretaker", "Neighbor", "Office"];

function useLineWrites(custno: string) {
  const qc = useQueryClient();
  const done = () => { qc.invalidateQueries({ queryKey: ["contacts"] }); qc.invalidateQueries({ queryKey: ["customer", custno] }); };
  const who = async () => (await supabase.auth.getUser()).data.user?.email ?? null;
  return {
    insert: async (row: Partial<ContactRow> & { name: string }) => {
      const { data, error } = await supabase.from("contact").insert({ custno, source: "manual", created_by: await who(), ...row }).select("id").single();
      if (error) throw error; done(); return (data as { id: string }).id;
    },
    update: async (id: string, patch: Partial<ContactRow>) => {
      const { error } = await supabase.from("contact").update({ ...patch, updated_at: new Date().toISOString() }).eq("id", id);
      if (error) throw error; done();
    },
    remove: async (id: string) => {
      const { error } = await supabase.from("contact").delete().eq("id", id);
      if (error) throw error; done();
    },
    hide: async (id: string, hidden: boolean) => {
      const { error } = await supabase.from("contact").update({ hidden_at: hidden ? new Date().toISOString() : null, hidden_by: hidden ? await who() : null, updated_at: new Date().toISOString() }).eq("id", id);
      if (error) throw error; done();
    },
  };
}

function PeopleSection({ customer, selectedLocation }: { customer: CustomerWithLocations; selectedLocation: LocationRow | null }) {
  const track = useTrack();
  const custno = customer.custno;
  const locno = selectedLocation?.locno ?? null;
  const canEdit = useCan("contacts.edit");
  const { data: contacts } = useContacts(custno, locno);
  const { data: owned } = useLocationOwnership(custno, locno);
  const era = useMemo(() => contactEraOf(owned), [owned]);
  const detailed = useMemo(() => buildLinesDetailed(customer, selectedLocation, contacts ?? []), [customer, selectedLocation, contacts]);
  const split = useMemo(() => (era ? splitLinesByEra(customer, selectedLocation, contacts ?? [], era) : null), [era, customer, selectedLocation, contacts]);
  const lines = split?.current ?? detailed.lines;
  const previous = split?.previous ?? [];
  const hidden = detailed.hidden;
  const consent = useCustomerConsent(custno, lines.filter((l) => l.phone).map((l) => ({ channel: "sms" as const, endpoint: l.phone as string })));
  const writes = useLineWrites(custno);
  const [editing, setEditing] = useState<string | null>(null); // line key
  const [adding, setAdding] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const [showPrev, setShowPrev] = useState(false);

  /** An ESC line becomes editable by making the Pegasus line that replaces it (same values). */
  const ensureRow = async (l: Line): Promise<string> => {
    if (l.contact) return l.contact.id;
    return writes.insert({
      name: l.name ?? customerFirstLast(customer, owned?.currentOwner?.name ?? null), role: l.role && !LINE_LABELS.includes(l.role) ? l.role : null,
      label: l.role && LINE_LABELS.includes(l.role) ? l.role : null,
      phone: l.phone, email: l.email, locno: l.origin === "esc-property" ? locno : null,
      replaces: lineKeys(l)[0] ?? null, source: "esc_correction",
    });
  };
  const toggleReach = (l: Line, k: "confirm" | "email" | "headsup") => track((async () => {
    const id = await ensureRow(l);
    if (k === "headsup") {
      const next = l.reach?.headsup === "call" ? "text" : l.reach?.headsup === "text" ? null : "call";
      await writes.update(id, { reach_headsup: next });
    } else if (k === "confirm") await writes.update(id, { reach_confirm: !l.reach?.confirm });
    else await writes.update(id, { reach_email: !l.reach?.email });
  })());
  const switchOff = (l: Line) => track((async () => {
    if (l.contact) await writes.hide(l.contact.id, true);
    else {
      // An ESC line switched off: a hidden Pegasus marker that replaces it — Bring back removes the marker.
      await writes.insert({ name: l.name ?? customerFirstLast(customer, owned?.currentOwner?.name ?? null), phone: l.phone, email: l.email, role: l.role, replaces: lineKeys(l)[0] ?? null, hidden_at: new Date().toISOString(), hidden_by: (await supabase.auth.getUser()).data.user?.email ?? null, source: "esc_switched_off", locno: l.origin === "esc-property" ? locno : null });
    }
  })());
  const bringBack = (l: Line) => track((async () => {
    if (!l.contact) return;
    if (l.contact.replaces && l.contact.source === "esc_switched_off") await writes.remove(l.contact.id);
    else await writes.hide(l.contact.id, false);
  })());

  return (
    <div>
      <SectionHead title="People & Lines" lead="everyone we can reach, every number and email — one list" />
      <div className="mt-4 flex flex-col gap-2">
        {lines.length === 0 && <div className="rounded-xl border border-dashed border-hairline px-4 py-5 text-center text-sm text-slate-400">No one on file yet — add a person or a number below.</div>}
        {lines.map((l) => (
          editing === l.key ? (
            <LineForm key={l.key} line={l} locno={locno} onCancel={() => setEditing(null)}
              onSave={(patch) => track((async () => { const id = await ensureRow(l); await writes.update(id, patch); setEditing(null); })())}
              onRemove={l.contact ? () => track((async () => { await writes.remove(l.contact!.id); setEditing(null); })()) : undefined} />
          ) : (
            <LineRow key={l.key} line={l} textsOk={l.phone ? !consent.blocked("sms", l.phone, "job_updates") : null} canEdit={canEdit} fallbackName={customerFirstLast(customer, owned?.currentOwner?.name ?? null)}
              onEdit={() => setEditing(l.key)} onToggleReach={(k) => void toggleReach(l, k)} onSwitchOff={() => void switchOff(l)}
              onAuthorize={() => track((async () => { const id = await ensureRow(l); await writes.update(id, { may_authorize: !l.mayAuthorize }); })())} />
          )
        ))}
        {adding ? (
          <LineForm line={null} locno={locno} onCancel={() => setAdding(false)}
            onSave={(patch) => track((async () => { await writes.insert({ ...patch, locno: patch.locno ?? null }); setAdding(false); })())} />
        ) : canEdit ? (
          <button type="button" onClick={() => setAdding(true)} className="flex items-center gap-2 rounded-xl border border-dashed border-slate-300 px-4 py-2.5 text-left text-[13px] font-bold text-brand-700 hover:bg-brand-50/40"><Plus className="h-4 w-4" /> Add a person or a number</button>
        ) : null}
      </div>
      {hidden.length > 0 && (
        <div className="mt-3 border-t border-slate-100 pt-2">
          <button type="button" onClick={() => setShowHidden((v) => !v)} className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700">
            {showHidden ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}{hidden.length} switched off
          </button>
          {showHidden && (
            <div className="mt-1.5 flex flex-col gap-1.5 opacity-80">
              {hidden.map((l) => (
                <div key={l.key} className="flex items-center gap-3 rounded-xl border border-hairline bg-slate-50 px-3.5 py-2 text-[13px]">
                  <span className="font-semibold text-slate-500 line-through">{l.phone ? prettyPhone(l.phone) : l.email}</span>
                  <span className="text-xs text-slate-400">{l.name}{l.role ? ` · ${l.role}` : ""}{l.contact?.hidden_at ? ` · switched off ${new Date(l.contact.hidden_at).toLocaleDateString([], { month: "short", day: "numeric" })}` : ""}{l.contact?.replaces ? " · ESC still lists it · Pegasus doesn't dial it" : ""}</span>
                  <button type="button" onClick={() => void bringBack(l)} className="ml-auto inline-flex items-center gap-1 rounded-md border border-brand-200 bg-white px-2 py-0.5 text-[11px] font-bold text-brand-700 hover:bg-brand-50"><RotateCcw className="h-3 w-3" /> Bring back</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {previous.length > 0 && (
        <div className="mt-3 border-t border-slate-100 pt-2">
          <button type="button" onClick={() => setShowPrev((v) => !v)} className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700">
            {showPrev ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}Previous owner · {previous.length} {previous.length === 1 ? "line" : "lines"} · not dialed
          </button>
          {showPrev && <div className="mt-1.5 flex flex-col gap-1.5 opacity-70">{previous.map((l) => <div key={l.key} className="rounded-xl border border-hairline px-3.5 py-2 text-[13px] text-slate-500">{[l.name, l.role, l.phone && prettyPhone(l.phone), l.email].filter(Boolean).join(" · ")}</div>)}</div>}
        </div>
      )}
      <p className="mt-3 text-[11.5px] leading-relaxed text-slate-400">An ESC line stays as ESC has it. Fixing one makes a Pegasus line that replaces it — ESC's copy is kept but hidden, and Bring back undoes it. Confirm text · Heads-up · Email mark who gets what when a visit is booked.</p>
    </div>
  );
}

function customerFirstLast(customer: CustomerWithLocations, ownerName: string | null) {
  return ownerName ?? displayCustomerName(null, customer.fullname);
}

function ReachChip({ on, label, onClick, disabled }: { on: boolean; label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-bold transition-colors disabled:cursor-default",
        on ? "border-brand-200 bg-brand-50 text-brand-700" : "border-hairline bg-white text-slate-400 hover:text-slate-700")}>
      {label}
    </button>
  );
}

function LineRow({ line: l, textsOk, canEdit, fallbackName, onEdit, onToggleReach, onSwitchOff, onAuthorize }: {
  line: Line; textsOk: boolean | null; canEdit: boolean; fallbackName: string; onEdit: () => void; onToggleReach: (k: "confirm" | "headsup" | "email") => void; onSwitchOff: () => void; onAuthorize: () => void;
}) {
  const tag: "esc" | "pegasus" | "owner" = l.origin === "pegasus-owner" ? "owner" : l.origin.startsWith("esc") ? "esc" : "pegasus";
  // ESC's slot label ("Cell", "Home") is the phone's label, not a person's role.
  const labelRole = !!l.role && LINE_LABELS.includes(l.role);
  const phoneLabel = l.label ?? (labelRole ? l.role : null);
  const role = [labelRole ? null : l.role, l.contact?.locno ? "this property" : null].filter(Boolean).join(" · ");
  const [menu, setMenu] = useState(false);
  return (
    <div className="grid grid-cols-[34px_minmax(0,1fr)_auto_auto] items-center gap-x-3 gap-y-1.5 rounded-xl border border-hairline bg-white px-3.5 py-2.5">
      <span className={cn("grid h-[34px] w-[34px] place-items-center rounded-[10px] text-xs font-extrabold text-white", tag === "esc" ? "bg-slate-300" : "bg-gradient-to-br from-brand-500 to-brand-800")}>{initialsOf(l.name)}</span>
      <div className="min-w-0">
        <div className="truncate text-sm font-bold text-slate-900">{l.name ?? <span className="font-semibold text-slate-500">{fallbackName}<span className="ml-1.5 text-[11px] font-medium text-slate-400">the account's line</span></span>}</div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          {role && <span>{role}</span>}
          <button type="button" onClick={onAuthorize} disabled={!canEdit} title="May approve work over the phone" className={cn("inline-flex items-center gap-1 text-[11px] font-bold", l.mayAuthorize ? "text-emerald-700" : "text-slate-300 hover:text-slate-500")}>
            <Star className="h-3 w-3" fill={l.mayAuthorize ? "currentColor" : "none"} /> may authorize work
          </button>
        </div>
      </div>
      <Tag kind={tag} />
      <div className="relative flex items-center gap-1">
        {canEdit && <button type="button" onClick={onEdit} title={l.contact ? "Edit" : "Correct this line in Pegasus"} className="grid h-7 w-7 place-items-center rounded-lg border border-hairline text-slate-500 hover:bg-slate-50 hover:text-slate-800"><PenLine className="h-3.5 w-3.5" /></button>}
        {canEdit && l.origin !== "pegasus-owner" && (
          <button type="button" onClick={() => setMenu((v) => !v)} onBlur={() => window.setTimeout(() => setMenu(false), 150)} className="grid h-7 w-7 place-items-center rounded-lg border border-hairline text-slate-500 hover:bg-slate-50 hover:text-slate-800" aria-label="More">···</button>
        )}
        {menu && (
          <div className="absolute right-0 top-8 z-10 w-44 rounded-xl border border-hairline bg-white p-1 shadow-e2">
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { setMenu(false); onSwitchOff(); }} className="block w-full rounded-lg px-2.5 py-1.5 text-left text-xs font-semibold text-slate-700 hover:bg-slate-50">Switch off<span className="block text-[10.5px] font-normal text-slate-400">kept on file · not shown, not dialed</span></button>
          </div>
        )}
      </div>
      <div className="col-span-3 col-start-2 flex flex-wrap items-center gap-x-5 gap-y-1.5">
        {l.phone && (
          <div className="flex items-center gap-2">
            <Phone className="h-3.5 w-3.5 text-slate-400" />
            <span className="text-[13.5px] font-semibold tabular-nums text-slate-900">{prettyPhone(l.phone)}{l.ext ? ` x${l.ext}` : ""}</span>
            <span className="text-[11.5px] text-slate-400">{phoneLabel}{textsOk === null ? null : <span className={cn("font-bold", phoneLabel && "ml-1.5", textsOk ? "text-emerald-700" : "text-rose-600")}>{textsOk ? "texts ✓" : "texts off"}</span>}</span>
          </div>
        )}
        {l.email && <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-slate-400" /><span className="text-[12.5px] font-semibold text-slate-800">{l.email}</span></div>}
        {!l.phone && !l.email && <span className="text-xs text-slate-400">no number or email</span>}
        <div className="ml-auto flex flex-wrap gap-1">
          <ReachChip on={!!l.reach?.confirm} label="Confirm text" onClick={() => onToggleReach("confirm")} disabled={!canEdit || !l.phone} />
          <ReachChip on={!!l.reach?.headsup} label={l.reach?.headsup === "text" ? "Heads-up text" : "Heads-up call"} onClick={() => onToggleReach("headsup")} disabled={!canEdit || !l.phone} />
          <ReachChip on={!!l.reach?.email} label="Email" onClick={() => onToggleReach("email")} disabled={!canEdit || !l.email} />
        </div>
      </div>
    </div>
  );
}

function LineForm({ line, locno, onSave, onCancel, onRemove }: {
  line: Line | null; locno: string | null;
  onSave: (patch: Partial<ContactRow> & { name: string }) => void; onCancel: () => void; onRemove?: () => void;
}) {
  const [name, setName] = useState(line?.name ?? "");
  const [role, setRole] = useState(line?.contact?.role ?? (line?.role && !LINE_LABELS.includes(line.role) ? line.role : "") ?? "");
  const [phone, setPhone] = useState(line?.phone ?? "");
  const [label, setLabel] = useState(line?.label ?? (line?.role && LINE_LABELS.includes(line.role) ? line.role : "") ?? "");
  const [email, setEmail] = useState(line?.email ?? "");
  const [thisProperty, setThisProperty] = useState(!!(line?.contact?.locno ?? (line?.origin === "esc-property" ? locno : null)));
  const [notes, setNotes] = useState(line?.contact?.notes ?? "");
  const [armed, setArmed] = useState(false);
  const canSave = (name.trim() || phone.trim() || email.trim()).length > 0;
  const submit = () => {
    if (!canSave) return;
    onSave({ name: name.trim() || (phone.trim() ? prettyPhone(phone.trim()) : email.trim()), role: role.trim() || null, phone: phone.trim() || null, label: label.trim() || null, email: email.trim() || null, notes: notes.trim() || null, locno: thisProperty && locno ? locno : null });
  };
  return (
    <div className="rounded-xl border border-brand-200 bg-brand-50/30 px-4 py-3" onKeyDown={(e) => { if (e.key === "Escape") onCancel(); if (e.key === "Enter" && (e.target as HTMLElement).tagName !== "TEXTAREA") { e.preventDefault(); submit(); } }}>
      <div className="grid grid-cols-[1fr_1fr] gap-x-4 gap-y-3">
        <Field label="Name"><input autoFocus value={name} onChange={(e) => setName(e.target.value)} className={INPUT} placeholder="Joe Lyden" /></Field>
        <Field label="Role"><input list="peg-line-roles" value={role} onChange={(e) => setRole(e.target.value)} className={INPUT} placeholder="Spouse · Tenant · Property manager" /><datalist id="peg-line-roles">{ROLE_SUGGESTIONS.map((r) => <option key={r} value={r} />)}</datalist></Field>
        <div className="grid grid-cols-[1fr_120px] gap-2">
          <Field label="Phone"><input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className={INPUT} placeholder="612-555-0100" /></Field>
          <Field label="Label"><input list="peg-line-labels" value={label} onChange={(e) => setLabel(e.target.value)} className={INPUT} placeholder="Cell" /><datalist id="peg-line-labels">{LINE_LABELS.map((r) => <option key={r} value={r} />)}</datalist></Field>
        </div>
        <Field label="Email"><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={INPUT} /></Field>
        <Field className="col-span-2" label="Note"><input value={notes} onChange={(e) => setNotes(e.target.value)} className={INPUT} placeholder="Calls back from work · prefers mornings" /></Field>
      </div>
      <div className="mt-3 flex items-center gap-3">
        {locno && <label className="flex cursor-pointer items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" checked={thisProperty} onChange={(e) => setThisProperty(e.target.checked)} className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600" /> Only this property</label>}
        {line && !line.contact && <span className="text-[11.5px] text-slate-400">Saving makes a Pegasus line that replaces ESC's — ESC's copy is kept, hidden</span>}
        <span className="ml-auto" />
        {onRemove && (armed ? <button type="button" onClick={onRemove} className="rounded-lg bg-rose-600 px-2.5 py-1.5 text-xs font-bold text-white">Remove?</button> : <button type="button" onClick={() => { setArmed(true); window.setTimeout(() => setArmed(false), 3500); }} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-400 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /> Remove</button>)}
        <button type="button" onClick={onCancel} className="text-xs font-semibold text-slate-500 hover:text-slate-800">Cancel</button>
        <button type="button" disabled={!canSave} onClick={submit} className="rounded-[10px] bg-brand-700 px-3.5 py-2 text-[12.5px] font-bold text-white hover:bg-brand-800 disabled:opacity-50">{line ? "Save" : "Add"}</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 3 · Reach
// ---------------------------------------------------------------------------
function ReachSection({ custno, onGoTab, onClose }: { custno: string; onGoTab?: (t: string) => void; onClose: () => void }) {
  const track = useTrack();
  const { data: pref } = useCommsPref(custno);
  const save = useSaveCommsPref();
  return (
    <div>
      <SectionHead title="Reach" lead="how we may contact this account" />
      <div className="mt-3 overflow-hidden rounded-xl border border-hairline">
        <CommsPrefSwitches custno={custno} />
      </div>
      <div className="mt-3 grid grid-cols-[1fr_auto] items-end gap-4">
        <Field label="Note" hint="Shown with the switches to anyone opening this customer">
          <TextField value={pref?.note ?? ""} onCommit={(v) => void track(save.mutateAsync({ custno, note: v || null }))} placeholder="Deaf — text only · husband handles scheduling" />
        </Field>
        <button type="button" onClick={() => { onClose(); onGoTab?.("comms"); }} className="inline-flex items-center gap-1.5 rounded-[10px] border border-hairline bg-white px-3 py-2 text-[12.5px] font-bold text-slate-700 hover:bg-slate-50">Customer portal · Comms tab <ArrowRight className="h-3.5 w-3.5" /></button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 4 · Properties
// ---------------------------------------------------------------------------
function PropertiesSection({ customer, selectedLocation, onLinkAction, onClose, onJumpWatch }: {
  customer: CustomerWithLocations; selectedLocation: LocationRow | null; onLinkAction?: EditCustomerPanelProps["onLinkAction"]; onClose: () => void; onJumpWatch: () => void;
}) {
  const track = useTrack();
  const qc = useQueryClient();
  const custno = customer.custno;
  const { data: signals } = useCustomerSignals(custno);
  const locs = (customer.locations ?? []).filter((l) => !l.location_inactive);
  const savePeg = (loc: LocationRow, patch: Record<string, unknown>) => track((async () => {
    const { error } = await supabase.from("pegasus_location").update({ ...patch, updated_at: new Date().toISOString() }).eq("custno", custno).eq("locno", loc.locno);
    if (error) throw error;
    qc.invalidateQueries({ queryKey: ["customer", custno] });
  })());
  const saveBill = (patch: Record<string, unknown>) => track((async () => {
    const { error } = await supabase.from("pegasus_customer").update({ ...patch, updated_at: new Date().toISOString() }).eq("custno", custno);
    if (error) throw error;
    qc.invalidateQueries({ queryKey: ["customer", custno] });
    qc.invalidateQueries({ queryKey: ["pegasus_customer_row", custno] });
  })());
  const billSame = !customer.add1 || (customer.add1 ?? "").trim().toLowerCase() === (selectedLocation?.add1 ?? "").trim().toLowerCase();
  const [billOpen, setBillOpen] = useState(false);
  return (
    <div>
      <SectionHead title="Properties" lead="where we go, and where the bill goes" />
      <div className="mt-4 flex flex-col gap-2.5">
        {locs.map((loc) => {
          const peg = !!loc.peg;
          const access = (signals ?? []).filter((s) => s.signal_type === "access_instruction" && s.status === "confirmed" && !s.hidden_at && !s.archived_at && (!s.locno || s.locno === loc.locno));
          const selected = selectedLocation?.locno === loc.locno;
          return (
            <div key={loc.locno} className={cn("grid grid-cols-[40px_minmax(0,1fr)_auto] items-start gap-3.5 rounded-[14px] border px-4 py-3.5", selected ? "border-brand-200 bg-brand-50/20" : "border-hairline bg-white")}>
              <span className="grid h-10 w-10 place-items-center rounded-[11px] bg-brand-50 text-brand-600"><Home className="h-[18px] w-[18px]" /></span>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <b className="text-sm text-slate-900">{loc.add1 ?? "No street address"}</b>
                  <span className="text-[12.5px] text-slate-500">{[loc.city, loc.state].filter(Boolean).join(", ")}{loc.zip ? ` ${loc.zip}` : ""}</span>
                  <Tag kind={peg ? "pegasus" : "esc"} />
                  {selected && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10.5px] font-bold text-emerald-700">Showing on the 360</span>}
                </div>
                {peg ? (
                  <div className="mt-2.5 grid grid-cols-[1fr_1fr_80px_110px] gap-x-3 gap-y-3">
                    <Field className="col-span-4" label="Name on the door"><TextField value={loc.locname ?? ""} onCommit={(v) => void savePeg(loc, { locname: v || null })} placeholder={displayCustomerName(null, customer.fullname)} /></Field>
                    <Field className="col-span-2" label="Street"><TextField value={loc.add1 ?? ""} onCommit={(v) => void savePeg(loc, { add1: v || null })} /></Field>
                    <Field label="State"><TextField value={loc.state ?? ""} onCommit={(v) => void savePeg(loc, { state: v || null })} /></Field>
                    <Field label="Zip"><TextField value={loc.zip ?? ""} onCommit={(v) => void savePeg(loc, { zip: v || null })} /></Field>
                    <Field className="col-span-2" label="City"><TextField value={loc.city ?? ""} onCommit={(v) => void savePeg(loc, { city: v || null })} /></Field>
                    <Field className="col-span-2" label="Property note"><TextField value={loc.notes ?? ""} onCommit={(v) => void savePeg(loc, { notes: v || null })} placeholder="Detached shop behind the house" /></Field>
                  </div>
                ) : (
                  <div className="mt-2 grid grid-cols-2 gap-x-5 gap-y-2">
                    <Field label="Name on the door"><div className={cn(INPUT, "bg-slate-50 text-slate-600")}>{loc.locname ?? <span className="text-slate-400">—</span>}</div></Field>
                    <Field label={<>Getting in <span className="font-semibold normal-case tracking-normal text-slate-400">· shows on the ticket</span></>}>
                      <div className={cn(INPUT, "flex flex-wrap items-center gap-x-2 gap-y-1 bg-slate-50")}>
                        {access.length ? access.map((a) => <span key={a.id} className="text-slate-700">{a.value}</span>) : <span className="text-slate-400">Nothing on file</span>}
                        <button type="button" onClick={onJumpWatch} className="ml-auto text-[11.5px] font-bold text-brand-700">Edit in Watch For ↓</button>
                      </div>
                    </Field>
                  </div>
                )}
                <div className={SUB}>{peg ? "A Pegasus property — fully editable here." : "The street address is ESC's until cutover — a wrong one is corrected in ESC and Pegasus follows on the next sync."}</div>
              </div>
              {peg ? <button type="button" onClick={() => { onClose(); onLinkAction?.("moveProperty", loc); }} className="text-xs font-bold text-slate-400 hover:text-slate-700">Move ▾</button> : <span />}
            </div>
          );
        })}

        <div className="grid grid-cols-[40px_minmax(0,1fr)_auto] items-start gap-3.5 rounded-[14px] border border-hairline bg-[#fafbfc] px-4 py-3.5">
          <span className="grid h-10 w-10 place-items-center rounded-[11px] bg-slate-100 text-slate-500"><Receipt className="h-[18px] w-[18px]" /></span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2"><b className="text-sm text-slate-900">Bills to</b>
              <span className="text-[12.5px] text-slate-500">{billSame ? "same as the service address" : [customer.add1, customer.add2, customer.city, customer.state, customer.zip].filter(Boolean).join(" · ")}</span>
              <Tag kind={customer.native ? "pegasus" : "esc"} />
            </div>
            {customer.native && billOpen && (
              <div className="mt-2.5 grid grid-cols-[1fr_1fr_80px_110px] gap-x-3 gap-y-3">
                <Field className="col-span-4" label="Bill to (name)"><TextField value={(customer as CustomerWithLocations & { bill_name?: string | null }).bill_name ?? ""} onCommit={(v) => void saveBill({ bill_name: v || null })} placeholder="A landlord, a property manager" /></Field>
                <Field className="col-span-2" label="Street"><TextField value={customer.add1 ?? ""} onCommit={(v) => void saveBill({ bill_add1: v || null })} /></Field>
                <Field label="State"><TextField value={customer.state ?? ""} onCommit={(v) => void saveBill({ bill_state: v || null })} /></Field>
                <Field label="Zip"><TextField value={customer.zip ?? ""} onCommit={(v) => void saveBill({ bill_zip: v || null })} /></Field>
                <Field className="col-span-2" label="City"><TextField value={customer.city ?? ""} onCommit={(v) => void saveBill({ bill_city: v || null })} /></Field>
              </div>
            )}
            <div className={SUB}>{customer.native ? "A different billing address (a landlord, a property manager) is set here." : "ESC's billing address — until cutover it is corrected in ESC."}</div>
          </div>
          {customer.native ? <button type="button" onClick={() => setBillOpen((v) => !v)} className="text-xs font-bold text-brand-700">{billOpen ? "Done" : "Different address"}</button> : <span />}
        </div>

        <button type="button" onClick={() => { onClose(); onLinkAction?.("addProperty"); }} className="flex items-center gap-2 rounded-xl border border-dashed border-slate-300 px-4 py-2.5 text-left text-[13px] font-bold text-brand-700 hover:bg-brand-50/40"><Plus className="h-4 w-4" /> Add a property under this customer</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 5 · Watch For — the editor you already have, embedded
// ---------------------------------------------------------------------------
function WatchSection({ customer, locno }: { customer: CustomerWithLocations; locno: string | null }) {
  const { data: statusFlag } = useCustomerStatusFlag(customer.custno);
  const dns = isDoNotService(statusFlag, customer.credit_rating);
  return (
    <div>
      <SectionHead title="Watch For" lead="what every dispatcher and tech sees before touching this account" />
      <div className="mt-3">
        <WatchOutsEditorBody custno={customer.custno} locno={locno} dns={dns} dnsReason={statusFlag?.dns_reason ?? null} inactiveReason={statusFlag?.inactive ? (statusFlag.reason ?? "") : null} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 7 · Plan
// ---------------------------------------------------------------------------
function PlanSection({ custno, onGoTab, onClose }: { custno: string; onGoTab?: (t: string) => void; onClose: () => void }) {
  const { data: ms } = useMembershipStatus(custno);
  const fmt = (d: string | null) => (d ? new Date(d + "T00:00:00").toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : null);
  const active = !!ms && ["active", "expiring", "fulfilled", "renewed"].includes(ms.status);
  // MEMBERSHIP-ESC-FULFILLED: the lifecycle word, said the way the office says it.
  const statusWord = ms?.status === "fulfilled" ? "all checks used" : ms?.status;
  return (
    <div>
      <SectionHead title="Plan" lead="the membership, edited where it lives — Manage Plan on the Membership tab" />
      <div className="mt-3 flex items-center gap-4 rounded-[14px] border border-hairline bg-white px-4 py-3.5">
        <div className="min-w-0 flex-1">
          {ms ? (
            <>
              <div className="text-sm font-bold text-slate-900">{ms.plan_name ?? "Plan"}{ms.term_years ? ` · ${ms.term_years}-year` : ""} <span className={cn("ml-1 rounded-full px-2 py-0.5 text-[10.5px] font-bold", active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500")}>{statusWord}</span></div>
              <div className="text-xs text-slate-500">{ms.expire_date ? `${ms.days_to_expire !== null && ms.days_to_expire < 0 ? "Ended" : ms.status === "fulfilled" ? "Paid through" : "Runs through"} ${fmt(ms.expire_date)}` : ""}{ms.checks_total ? ` · ${ms.checks_used} of ${ms.checks_total} checks used` : ""}{ms.next_check ? ` · next: ${ms.next_check}` : ""} · {ms.source === "esc" ? "ESC agreement" : "sold in Pegasus"}</div>
            </>
          ) : (
            <><div className="text-sm font-bold text-slate-900">No plan on file</div><div className="text-xs text-slate-500">Price & Build sells one; it is managed on the Membership tab afterwards.</div></>
          )}
        </div>
        <button type="button" onClick={() => { onClose(); onGoTab?.("membership"); }} className="inline-flex items-center gap-1.5 rounded-[10px] bg-brand-700 px-3.5 py-2 text-[12.5px] font-bold text-white hover:bg-brand-800">{ms ? "Manage Plan" : "Price & Build"} <ArrowRight className="h-3.5 w-3.5" /></button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 8 · Notes & Snapshot
// ---------------------------------------------------------------------------
function NotesSection({ customer, locno }: { customer: CustomerWithLocations; locno: string | null }) {
  const track = useTrack();
  const custno = customer.custno;
  const { data: snap } = useSnapshotNote(custno, locno);
  const saveSnap = useSaveSnapshotNote();
  const { data: notes } = usePegasusNotes(custno);
  const addNote = useAddPegasusNote();
  const delNote = useDeletePegasusNote();
  const [draft, setDraft] = useState("");
  const [armed, setArmed] = useState<string | null>(null);
  const [snapDraft, setSnapDraft] = useState<string | null>(null);
  const snapValue = snapDraft ?? snap?.body ?? "";
  const commitSnap = () => { if (snapDraft !== null && snapDraft.trim() !== (snap?.body ?? "").trim() && locno) void track(saveSnap.mutateAsync({ custno, locno, body: snapDraft.trim() })); setSnapDraft(null); };
  return (
    <div>
      <SectionHead title="Notes & Snapshot" lead="what the office adds — ESC's notes stay read-only on the Notes tab" />
      <div className="mt-4 grid gap-4">
        <Field label={<>Office correction on the Snapshot <span className="font-semibold normal-case tracking-normal text-slate-400">· this property</span></>} hint="Shown under the Pegasus Snapshot — the AI summary is re-read against it">
          <textarea value={snapValue} onChange={(e) => setSnapDraft(e.target.value)} onBlur={commitSnap} rows={2} disabled={!locno} placeholder="The furnace was replaced in 2025 — the 2019 unit is gone" className={cn(INPUT, "resize-none leading-snug")} />
        </Field>
        <div>
          <div className={LABEL}>Pegasus notes <span className="rounded-full bg-slate-100 px-1.5 text-[10px] font-extrabold text-slate-500">{notes?.length ?? 0}</span></div>
          <div className="mt-1.5 flex flex-col gap-1.5">
            {(notes ?? []).slice(0, 8).map((n) => (
              <div key={n.id} className="flex items-start gap-3 rounded-xl border border-hairline bg-white px-3.5 py-2">
                <div className="min-w-0 flex-1">
                  <div className="whitespace-pre-wrap text-[13px] text-slate-800">{n.body}</div>
                  <div className="mt-0.5 text-[11px] text-slate-400">{n.author ?? "office"} · {new Date(n.created_at).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}</div>
                </div>
                {armed === n.id ? <button type="button" onClick={() => void track(delNote.mutateAsync({ id: n.id, custno }))} className="rounded-md bg-rose-600 px-2 py-0.5 text-[11px] font-bold text-white">Delete?</button>
                  : <button type="button" onClick={() => { setArmed(n.id); window.setTimeout(() => setArmed(null), 3500); }} className="text-slate-300 hover:text-rose-600" aria-label="Delete note"><X className="h-3.5 w-3.5" /></button>}
              </div>
            ))}
            {(notes?.length ?? 0) > 8 && <div className="text-[11.5px] text-slate-400">{(notes?.length ?? 0) - 8} more on the Notes tab</div>}
            <div className="flex items-start gap-2">
              <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} placeholder="Type it the way you'd say it on the phone — Pegasus re-reads notes for watch-outs" className={cn(INPUT, "mt-0 flex-1 resize-none leading-snug")}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && draft.trim()) { e.preventDefault(); void track(addNote.mutateAsync({ custno, locno, body: draft })).then(() => setDraft("")); } }} />
              <button type="button" disabled={!draft.trim() || addNote.isPending} onClick={() => void track(addNote.mutateAsync({ custno, locno, body: draft })).then(() => setDraft(""))} className="h-[42px] rounded-[10px] bg-brand-700 px-4 text-sm font-bold text-white hover:bg-brand-800 disabled:opacity-50">Add</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 9 · Links & History
// ---------------------------------------------------------------------------
function LinksSection({ onLinkAction, onClose, hasPrevious }: { onLinkAction?: EditCustomerPanelProps["onLinkAction"]; onClose: () => void; hasPrevious: boolean }) {
  const canTransfer = useCan("customers.transfer_owner");
  const canMerge = useCan("customers.merge");
  const go = (k: "transfer" | "link" | "merge" | "previous") => { onClose(); onLinkAction?.(k); };
  const Row = ({ title, sub, onClick, disabled }: { title: string; sub: string; onClick: () => void; disabled?: boolean }) => (
    <button type="button" onClick={onClick} disabled={disabled} className="flex items-center gap-3 rounded-xl border border-hairline bg-white px-4 py-3 text-left hover:bg-slate-50 disabled:opacity-50">
      <div className="min-w-0 flex-1"><div className="text-sm font-bold text-slate-900">{title}</div><div className="text-xs text-slate-500">{sub}</div></div>
      <ArrowRight className="h-4 w-4 text-slate-400" />
    </button>
  );
  return (
    <div>
      <SectionHead title="Links & History" lead="the house, its owners, and other accounts" />
      <div className="mt-3 grid grid-cols-2 gap-2">
        <Row title="New owner" sub="Record a sale or a tenant — the handoff archives the old owner's lines" onClick={() => go("transfer")} disabled={!canTransfer} />
        <Row title="Link another account to this house" sub="Same address, two ESC accounts — nothing merges, nothing moves" onClick={() => go("link")} />
        <Row title="Merge into another account" sub="Duplicate records — everything lands on the one you keep" onClick={() => go("merge")} disabled={!canMerge} />
        <Row title="Previous owners" sub={hasPrevious ? "Who had this property before, and how we reached them" : "None recorded yet"} onClick={() => go("previous")} disabled={!hasPrevious} />
      </div>
    </div>
  );
}
